import {
  DEFAULT_GROUP_DATA,
  SEARCH_BY_FIELD,
  SEARCH_LEVEL,
  COGNITO_EMAIL_VALIDATION,
  BRAND_PROFILES,
  ONLINE_ACCOUNT_TYPES,
} from 'constants/index';
import isEmpty from 'lodash/isEmpty';
import _set from 'lodash/set';
import _unset from 'lodash/unset';
import {
  profileService,
  cmdProfileService,
  cmdProfileServiceV3,
  cmdProfileServiceV4,
  clientsService,
  clientsCentralSearchService,
  clientsCentralSearchServiceV2,
  groupsService,
  cancelRequest,
  conversationService,
  nmProfilePaymentUCAService,
  nmProfileAddressUCAService,
  bgProfilePaymentUCAService,
  bgProfileAddressUCAService,
} from 'config/api';
import clientAuth from 'config/clientAuth';
import {
  getRecentSearches,
  preparePaymentsForUpdate,
  convertAurusCardType,
  mapUcaAddressToCfa,
  mapCfaAddressToUca,
  mapCfaPaymentToUca,
  mapUcaPaymentsToCfa,
} from 'utils';
import { addNotification } from 'modules/shell/actions';
import { splitPlccAndNoPlccKiNumbers } from '../reducers/utils';
import actionTypes from './actionTypes';
import { fetchClientReminders } from './reminderActions';
import { retrieveAttributeValuesFromPreferences, retrieveAllValuesFromPreferences } from '../utils';
import { zeroDollarAuth, getClientProfileDataFromUCA } from '../services';

/**
 * @typedef {Object} SearchCentralClientsParam
 * @property {string} search,
 * @property {string} field,
 * @property {string} searchLevel,
 * @property {number} page,
 * @property {boolean} withAddClientButton,
 */

const MAX_DISPLAY_SEARCH_RESULTS = 1000;

export const getClient =
  ({ clientId, isClientInformationFromUcaEnabled, hardReload, isBGAssociate, isBGFarfetchEnabled = false }) =>
  async (dispatch, getState) => {
    const state = getState();
    const client = state.clientsModule.clientDetails.data;
    const {
      user: {
        data: { employeeNumber },
      },
    } = state;

    if (client && client.clientId === clientId && !hardReload) {
      dispatch({
        type: actionTypes.CLIENT_DETAILS_SUCCESS,
        payload: client,
      });
      return;
    }

    dispatch({
      type: actionTypes.CLIENT_DETAILS_LOADING,
    });

    try {
      let clientDetailsData = null;
      if (isClientInformationFromUcaEnabled) {
        const { shippingAddress, payments, billingAddress, profileAttributes } = await getClientProfileDataFromUCA({
          clientId,
          isBGAssociate,
          employeeNumber,
          isBGFarfetchEnabled,
        });

        clientDetailsData = {
          ...profileAttributes,
          payments,
          shippingAddress,
          billingAddress,
        };
      } else {
        const { data } = await profileService.get(`customers/${clientId}`, { clientId });

        clientDetailsData = {
          clientId,
          ...data,
        };
      }

      dispatch({
        type: actionTypes.CLIENT_DETAILS_SUCCESS,
        payload: clientDetailsData,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.CLIENT_DETAILS_ERROR,
        payload: error,
      });
    }
  };

/**
 * @param {string} [cmdUri]
 */
export const getCMDProfile =
  (cmdUri, { isInHouseCmdApi = true, isClientInformationFromUcaEnabled, isClientProfileRedesign = false } = {}) =>
  async (dispatch, getState) => {
    const { profileData } = getState().clientsModule.clientDetails;
    if (profileData && profileData.cmdUri === cmdUri) {
      dispatch({
        type: actionTypes.CMD_PROFILE_SUCCESS,
        payload: {
          ...profileData,
          isClientInformationFromUcaEnabled,
        },
      });

      dispatch({
        type: actionTypes.GET_CLIENT_GROUPS_SUCCESS,
        payload: profileData.groups,
      });

      dispatch({
        type: actionTypes.CLIENT_LOYALTY_SUCCESS,
        payload: profileData.loyaltyData,
      });
      return;
    }

    dispatch({
      type: actionTypes.CMD_PROFILE_LOADING,
    });

    try {
      const additionalQueryString = isInHouseCmdApi ? '?internalStorage=true' : '';
      const apiEndPoint = isClientProfileRedesign ? cmdProfileServiceV4 : cmdProfileServiceV3;
      const res = await apiEndPoint.get(`profiles/${cmdUri}${additionalQueryString}`);

      dispatch({
        type: actionTypes.CMD_PROFILE_SUCCESS,
        payload: {
          ...res.data,
          cmdUri,
          isClientInformationFromUcaEnabled,
        },
      });

      dispatch({
        type: actionTypes.CMD_PROFILE_DETAILS_SUCCESS,
        payload: {
          ...res.data,
          cmdUri,
        },
      });

      dispatch({
        type: actionTypes.CLIENT_LOYALTY_SUCCESS,
        payload: res.data.loyaltyData,
      });

      dispatch({ type: actionTypes.GET_CLIENT_BADGES_SUCCESS, payload: res.data.badges });

      dispatch({ type: actionTypes.SET_CLIENT_CONNECTS, payload: res.data.connects });

      if (res.data.groups) {
        dispatch({ type: actionTypes.GET_CLIENT_GROUPS_SUCCESS, payload: res.data.groups });
      }
    } catch (error) {
      dispatch({
        type: actionTypes.CMD_PROFILE_ERROR,
        payload: error,
      });
    }
  };

export const updateClientWithCMDProfile = (cmdURI) => async (dispatch, getState) => {
  const clients = getState().clientsModule.clientsList.data;
  const { profileData } = getState().clientsModule.clientDetails;
  let needToUpdate = false;
  const updatedClients = clients.map((client) => {
    if (client.profileId === cmdURI && !client.phones && !client.emails) {
      needToUpdate = true;
      return { ...client, ...profileData };
    }
    return client;
  });
  if (needToUpdate) {
    dispatch({
      type: actionTypes.CLIENTS_LIST_SUCCESS,
      payload: updatedClients,
    });
  }
};

const createNMAccount = async (params) => {
  return clientAuth.signUp(params);
};

const createBGAccount = async (params) => {
  const { attributes } = params;
  const body = {
    firstName: attributes.given_name,
    lastName: attributes.family_name,
    email: params.username,
  };
  try {
    const res = await profileService.post('/customers', body, {
      httpClientOptions: { handleErrors: false },
    });
    return {
      userSub: res.data.profileId,
    };
  } catch (err) {
    const { message } = err.response.data;
    const isExistsMsg = message.includes('already exists');
    const convertedErr = new Error(message);
    if (isExistsMsg) {
      convertedErr.name = COGNITO_EMAIL_VALIDATION.RESPONSE_TYPES.EXISTS;
    } else {
      convertedErr.name = COGNITO_EMAIL_VALIDATION.RESPONSE_TYPES.INVALID;
    }
    throw convertedErr;
  }
};

export const createOnlineAccount =
  (
    { cmdUri, phone, sourceName, firstName, lastName, creationMessage, isUCA, ...params },
    bgAccountCreationFlowEnabled = false,
  ) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.CREATE_ONLINE_ACCOUNT_LOADING });

    const { username: email } = params;

    try {
      let res = null;
      if (!bgAccountCreationFlowEnabled) {
        if (sourceName && sourceName === BRAND_PROFILES.BG) {
          res = await createBGAccount(params);
        } else {
          res = await createNMAccount(params);
        }
      } else {
        res = await clientAuth.signUp(params);
      }

      try {
        const body = {
          onlineProfileId: res.userSub,
          cmdUri,
          email,
          sourceName,
          phone,
          firstName,
          lastName,
        };

        const response = await profileService.post('/profiles', body);

        const newProfile = {
          profileId: response.data.onlineProfileId,
          phone: response.data.phone,
          sourceName: response.data.sourceName,
          profileType: `${response.data.sourceName}O`,
          email: response.data.email,
          newCreated: true,
          ...((bgAccountCreationFlowEnabled || response.data.sourceName === BRAND_PROFILES.NM) && {
            type: ONLINE_ACCOUNT_TYPES.UNIVERSAL_CUSTOMER_ACCOUNT,
          }),
        };
        const successActionType = isUCA
          ? actionTypes.CREATE_UCA_ONLINE_ACCOUNT_SUCCESS
          : actionTypes.CREATE_ONLINE_ACCOUNT_SUCCESS;
        dispatch({ type: successActionType, payload: newProfile });
      } catch (error) {
        dispatch({ type: actionTypes.CREATE_ONLINE_ACCOUNT_ERROR });
      }

      dispatch(
        addNotification({
          id: `create-profile-${Date.now()}`,
          message: creationMessage || 'Online account was successfully created',
          type: 'success',
        }),
      );

      return res;
    } catch (error) {
      try {
        dispatch({ type: actionTypes.CREATE_ONLINE_ACCOUNT_ERROR });
        const { name, message } = error;

        const isExistsMsg = message.includes('already exists');
        const isCMDEmailExists = name === COGNITO_EMAIL_VALIDATION.RESPONSE_TYPES.EXISTS || isExistsMsg;
        const isEmailExists = name === COGNITO_EMAIL_VALIDATION.RESPONSE_TYPES.INVALID && isExistsMsg;
        const isInvalidEmail = name === COGNITO_EMAIL_VALIDATION.RESPONSE_TYPES.INVALID && !isExistsMsg;

        if (isCMDEmailExists) {
          const onlineProfile = await profileService.post('/customerByEmail', { email });

          const { profileId } = onlineProfile.data;

          try {
            const cmdProfile = await cmdProfileService.get(`/profiles/${profileId}?sourceName=${sourceName}`, {
              httpClientOptions: { handleErrors: false },
            });

            return {
              type: COGNITO_EMAIL_VALIDATION.ERROR_TYPES.ERROR,
              onlineProfile: onlineProfile.data,
              cmdProfile: cmdProfile.data,
            };
          } catch (e) {
            // Specially for case cases when an online account for a client with a specific email already exists in ATG
            // but it may not have relevant information to tie it a CMD profile
            // More details in https://neimanmarcus.atlassian.net/browse/OSP-3740
            return {
              type: COGNITO_EMAIL_VALIDATION.ERROR_TYPES.ERROR,
              onlineProfile: onlineProfile.data,
              cmdProfile: null,
            };
          }
        }

        if (isEmailExists) {
          return { type: COGNITO_EMAIL_VALIDATION.ERROR_TYPES.EXISTED };
        }

        if (isInvalidEmail) {
          return { type: COGNITO_EMAIL_VALIDATION.ERROR_TYPES.INVALID };
        }
      } catch (err) {
        dispatch({ type: actionTypes.CREATE_ONLINE_ACCOUNT_ERROR });
        return { profile: null, type: COGNITO_EMAIL_VALIDATION.ERROR_TYPES.ERROR };
      }
    }
  };

export const updateOnlineAccount =
  (
    customerId,
    { firstName, lastName /* TODO: could be extended */ },
    options = {
      brand: BRAND_PROFILES.NM,
      linkToCmd: false,
      cmdUri: null,
    },
  ) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.UPDATE_ONLINE_ACCOUNT_LOADING });

    try {
      const body = {};

      if (firstName !== undefined) body.firstName = firstName;
      if (lastName !== undefined) body.lastName = lastName;

      const res = await profileService.put(`customers/${customerId}`, body);

      if (options.linkToCmd) {
        const { data: onlineProfile } = res;

        await profileService.post('/profiles', {
          cmdUri: options.cmdUri,
          sourceName: options.brand,
          onlineProfileId: onlineProfile.profileId,
          email: onlineProfile.emailAddress,
          phone: onlineProfile.shippingAddress[0]?.dayTelephone,
          firstName,
          lastName,
        });
      }

      dispatch({
        type: actionTypes.UPDATE_ONLINE_ACCOUNT_SUCCESS,
        payload: { ...res.data, sourceName: options.brand },
      });

      return res.data;
    } catch (err) {
      dispatch({
        type: actionTypes.UPDATE_ONLINE_ACCOUNT_FAILURE,
        payload: err,
      });

      throw err;
    }
  };

export const fetchClients = () => async (dispatch) => {
  dispatch({
    type: actionTypes.CLIENTS_LIST_LOADING,
  });

  try {
    const res = await clientsService.get('/customers');
    dispatch({ type: actionTypes.CLIENTS_LIST_SUCCESS, payload: res.data });
  } catch (error) {
    dispatch({
      type: actionTypes.CLIENTS_LIST_ERROR,
      payload:
        error.response?.data?.message || error?.message || 'Not able to get client list, please try again later.',
    });
  }
};

export const setFilter = (value) => {
  return {
    type: actionTypes.CLIENTS_SET_FILTER,
    payload: value,
  };
};

export const setClientsSearch = (value) => {
  return {
    type: actionTypes.CLIENTS_SET_SEARCH,
    payload: value,
  };
};

/** @param {SearchCentralClientsParam} searchCentralClientsParam */
export const searchCentralClients =
  ({
    search = '',
    field = SEARCH_BY_FIELD.PHONE,
    searchLevel = SEARCH_LEVEL.CLIENT_LIST,
    page = 1,
    withAddClientButton = false,
  }) =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.CLIENTS_CENTRAL_SEARCH_LOADING,
    });
    cancelRequest.cancel();
    let noRecordsInClientList = false;
    try {
      let res;
      if (search) {
        res = await clientsCentralSearchService.get('/customerSearch', {
          cancelable: true,
          params: {
            q: search,
            field,
            searchLevel,
            page,
          },
        });

        const shouldSendClientListRequest =
          withAddClientButton && searchLevel === SEARCH_LEVEL.GLOBAL && page === 1 && res.data?.data.length === 0;

        if (shouldSendClientListRequest) {
          const clientListRes = await clientsCentralSearchService.get('/customerSearch', {
            cancelable: true,
            params: {
              q: search,
              field,
              searchLevel: SEARCH_LEVEL.CLIENT_LIST,
              page,
            },
          });

          if (clientListRes.data?.data.length === 0) {
            noRecordsInClientList = true;
          }
        }
      } else {
        res = await clientsCentralSearchService.get(`/myCustomers?page=${page}`);
      }

      dispatch({
        type: actionTypes.CLIENTS_CENTRAL_SEARCH_SUCCESS,
        payload: {
          ...res.data,
          noRecordsInClientList,
          page,
        },
      });
    } catch (error) {
      if (error.toString() === 'Cancel') {
        return;
      }

      dispatch({
        type: actionTypes.CLIENTS_CENTRAL_SEARCH_ERROR,
        payload:
          error.response?.data?.message ||
          error?.message ||
          'Not able to get client central list, please try again later.',
      });
    }
  };

export const getGlobalClients =
  (
    search = '',
    { searchField, from, size, includeClientList } = {
      from: 0,
      size: 24,
      searchField: 'name',
      includeClientList: true,
    },
    shouldCheckClientListIfEmpty,
  ) =>
  async (dispatch, getState) => {
    const currentState = getState();
    let noRecordsInClientList = false;

    if (isEmpty(currentState.clientsModule.clientsList.clients)) {
      dispatch({ type: actionTypes.CLIENTS_INFINITE_LIST_LOADING, data: true });
    }

    try {
      const params = {
        q: search || "''",
        field: searchField,
        from,
        size,
        includeClientList,
      };
      const {
        data: { data },
      } = await clientsCentralSearchServiceV2.get('/customers', { params });
      const customers = {};
      const shouldSendClientListRequest =
        shouldCheckClientListIfEmpty && params.from === 0 && data.customers.length === 0;

      if (shouldSendClientListRequest) {
        noRecordsInClientList = true;
      }
      // Customers array is transformed into map with relevant elements positions
      // (because we can load customers from 0 to 30, and after it from 100 to 120, and they should be saved appropriately
      for (let i = params.from, j = 0; i <= params.from + params.size; i += 1, j += 1) {
        if (data.customers[j]) {
          customers[i] = data.customers[j];
        }
      }

      const totalCount = data.total > MAX_DISPLAY_SEARCH_RESULTS ? MAX_DISPLAY_SEARCH_RESULTS : data.total;

      dispatch({
        type: actionTypes.CLIENTS_INFINITE_LIST_SUCCESS,
        data: {
          customers,
          noRecordsInClientList,
        },
      });
      dispatch({
        type: actionTypes.CLIENTS_INFINITE_LIST_STATS,
        data: {
          stats: { totalCount },
        },
      });
    } catch (error) {
      dispatch({
        type: actionTypes.GET_GROUP_CLIENTS_ERROR,
        payload:
          error.response?.data?.message ||
          error?.message ||
          'Not able to get client central list, please try again later.',
      });
    }
  };

export const getGroupClients =
  (groupId, search = '', page = 1) =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.CLIENTS_CENTRAL_SEARCH_LOADING,
    });
    let res;

    try {
      if (groupId === DEFAULT_GROUP_DATA.groupId) {
        if (search) {
          res = await clientsCentralSearchService.get(
            `/customerSearch?q=${search}&page=${page}&searchLevel=clientList`,
          );
        } else {
          res = await clientsCentralSearchService.get(`/myCustomers?page=${page}`);
        }
      } else {
        res = await groupsService.get(`/groups/${groupId}/customers`);
      }

      dispatch({ type: actionTypes.CLIENTS_CENTRAL_SEARCH_SUCCESS, payload: { ...res.data, page } });
    } catch (error) {
      dispatch({
        type: actionTypes.CLIENTS_CENTRAL_SEARCH_ERROR,
        payload:
          error.response?.data?.message ||
          error?.message ||
          'Not able to get client central list, please try again later.',
      });
    }
  };

export const clearCentralClients = () => ({
  type: actionTypes.CLIENTS_CENTRAL_SEARCH_CLEAR,
});

export const fetchRecentClients = () => async (dispatch) => {
  dispatch({
    type: actionTypes.CLIENTS_RECENT_LIST_LOADING,
  });

  try {
    const res = getRecentSearches();

    dispatch({ type: actionTypes.CLIENTS_RECENT_LIST_SUCCESS, payload: res });
  } catch (error) {
    dispatch({
      type: actionTypes.CLIENTS_RECENT_LIST_ERROR,
      payload:
        error.response?.data?.message ||
        error?.message ||
        'Not able to get recent client list, please try again later.',
    });
  }
};

export const updateRecentClients = (client) => async (dispatch) => {
  dispatch({
    type: actionTypes.CLIENTS_RECENT_LIST_LOADING,
  });

  try {
    const list = JSON.parse(window.localStorage.getItem('recentClients')) || []; // TODO rework to service

    if (!list.find((u) => u.profileId === client.profileId)) {
      list.push(client);
    }

    window.localStorage.setItem('recentClients', JSON.stringify(list));

    dispatch({ type: actionTypes.CLIENTS_RECENT_LIST_UPDATE, payload: list });
  } catch (error) {
    dispatch({
      type: actionTypes.CLIENTS_RECENT_LIST_ERROR,
      payload:
        error.response?.data?.message ||
        error?.message ||
        'Not able to get recent client list, please try again later.',
    });
  }
};

export const addShipping =
  ({ address, isBilling, isBGAssociate, isClientInformationFromUcaEnabled }) =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.ADD_SHIPPING_LOADING,
      payload: address,
    });

    const { clientId, line2 = '', defaultAddress = false } = address;
    const body = {
      ...address,
      line2,
      defaultAddress,
    };

    try {
      if (isClientInformationFromUcaEnabled) {
        const ucaShippingAddressPayload = {
          ...mapCfaAddressToUca(address),
          defaultAddress: address.defaultAddress || false,
          addressType: isBilling ? 'billing' : 'shipping',
          accountId: clientId,
        };
        const ucaService = isBGAssociate ? bgProfileAddressUCAService : nmProfileAddressUCAService;
        const { data } = await ucaService.post(`/${clientId}/addresses`, ucaShippingAddressPayload);

        dispatch({
          type: actionTypes.ADD_SHIPPING_SUCCESS,
          payload: mapUcaAddressToCfa(data),
        });
      } else {
        await profileService.post(`customers/${clientId}/shipping`, body, { clientId });
        dispatch({
          type: actionTypes.ADD_SHIPPING_SUCCESS,
          payload: body,
        });
      }
    } catch (error) {
      dispatch({
        type: actionTypes.ADD_SHIPPING_ERROR,
        payload: error,
      });
    }
  };

export const editShipping =
  ({ profileId, address, email, isDefault, isClientInformationFromUcaEnabled, isBGAssociate }) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.EDIT_SHIPPING_LOADING, payload: isClientInformationFromUcaEnabled ? address : null });
    try {
      if (isClientInformationFromUcaEnabled) {
        const { defaultAddress, addressId } = address;
        const ucaAddressPayload = {
          ...mapCfaAddressToUca(address),
          accountId: profileId,
          defaultAddress: isDefault || defaultAddress || false,
        };

        const ucaService = isBGAssociate ? bgProfileAddressUCAService : nmProfileAddressUCAService;
        await ucaService.put(`/${profileId}/addresses/${addressId}`, ucaAddressPayload);

        dispatch({
          type: actionTypes.EDIT_UCA_ADDRESS_SUCCESS,
          payload: address,
        });
      } else {
        const {
          line1,
          line2 = '',
          firstName,
          lastName,
          city,
          state,
          zip,
          phone,
          defaultAddress,
          contactAddressName,
          country,
        } = address;

        const body = {
          email,
          line1,
          line2,
          firstName,
          lastName,
          city,
          state,
          country: country || '',
          zip,
          phone,
          contactAddressName,
          defaultAddress: isDefault || defaultAddress || false,
        };

        await profileService.put(`/customers/${profileId}/shipping`, body);

        dispatch({
          type: actionTypes.EDIT_SHIPPING_SUCCESS,
          payload: address,
        });
      }

      return 'success';
    } catch (err) {
      dispatch({
        type: actionTypes.EDIT_SHIPPING_ERROR,
        payload: err,
      });
      return 'error';
    }
  };

export const editPayments =
  ({ profileId, paymentMethod, isDefault, isClientInformationFromUcaEnabled, isBGAssociate }) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.EDIT_PAYMENTS_LOADING });

    try {
      const isDefaultCart = isDefault ? true : paymentMethod.defaultCard;
      const body = { ...paymentMethod, defaultCard: isDefaultCart };

      if (isClientInformationFromUcaEnabled) {
        const ucaPaymentPayload = mapCfaPaymentToUca(profileId, body, true);
        /**
         * TODO: Remove this block with an address update call when UCA api is ready
         */
        const { billingAddress } = paymentMethod;
        const ucaAddressPayload = {
          ...mapCfaAddressToUca(billingAddress),
          accountId: profileId,
          defaultAddress: isDefaultCart,
        };

        const ucaAddressService = isBGAssociate ? bgProfileAddressUCAService : nmProfileAddressUCAService;
        const updateAddressPromise = ucaAddressService.put(
          `/${profileId}/addresses/${billingAddress.addressId}`,
          ucaAddressPayload,
        );

        const ucaPaymentService = isBGAssociate ? bgProfilePaymentUCAService : nmProfilePaymentUCAService;
        const updatePaymentPromise = ucaPaymentService.put(
          `/${profileId}/payments/${ucaPaymentPayload.paymentId}`,
          ucaPaymentPayload,
        );

        await Promise.all([updateAddressPromise, updatePaymentPromise]);
      } else {
        await profileService.put(`/customers/${profileId}/payments`, body);
      }

      dispatch({
        type: actionTypes.EDIT_PAYMENTS_SUCCESS,
        payload: {
          ...body,
          isClientInformationFromUcaEnabled,
        },
      });
      return 'success';
    } catch (err) {
      dispatch({
        type: actionTypes.EDIT_PAYMENTS_ERROR,
        payload: err,
      });
      return 'error';
    }
  };

export const changePaymentsErrorReset = () => ({ type: actionTypes.CHANGE_PAYMENTS_ERROR_RESET });

const preventDuplicatePayments = (payments, zeroDollarResponseData) => {
  let isDuplicate = false;

  payments.forEach((payment) => {
    if (payment?.keyIdentifier === zeroDollarResponseData.keyIdentifier) isDuplicate = true;
  });

  return isDuplicate;
};

export const changePayments =
  ({ paymentData = {}, isNewCard = false, isClientInformationFromUcaEnabled = false, isBGAssociate }) =>
  async (dispatch, getState) => {
    const { clientDetails } = getState().clientsModule;
    if (isEmpty(paymentData)) return;

    let zeroDollarResponse = {};

    dispatch({ type: actionTypes.CHANGE_PAYMENTS_LOADING });

    const { data: clientDetailsData = {} } = clientDetails;
    const { clientId, payments = [] } = clientDetailsData;

    if (isNewCard) {
      zeroDollarResponse = await zeroDollarAuth(paymentData, clientId);
    }

    const { data: zeroDollarResponseData, error: zeroDollarResponseError } = zeroDollarResponse;

    if (zeroDollarResponseData && preventDuplicatePayments(payments, zeroDollarResponseData)) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Card already present on client account.',
        }),
      );

      return 'duplicate';
    }

    if (isEmpty(zeroDollarResponseData) && isNewCard) {
      dispatch({ type: actionTypes.CHANGE_PAYMENTS_ERROR, payload: zeroDollarResponseError });
    } else {
      const { payments = [] } = clientDetailsData;
      const { foundPayment, updatedPayments, updatedSelectedPayment } = preparePaymentsForUpdate({
        payments,
        selectedPayment: paymentData,
        zeroDollarResponseData,
        isClientInformationFromUcaEnabled,
      });

      try {
        if (isNewCard) {
          if (isClientInformationFromUcaEnabled) {
            const ucaPaymentPayload = mapCfaPaymentToUca(clientId, foundPayment || updatedSelectedPayment);

            const ucaService = isBGAssociate ? bgProfilePaymentUCAService : nmProfilePaymentUCAService;
            const { data } = await ucaService.post(`/${clientId}/payments`, ucaPaymentPayload);
            const newPayment = mapUcaPaymentsToCfa([data])[0];

            // update redux state with new payment
            if (!foundPayment) {
              updatedPayments.push({
                ...newPayment,
                billingAddress: updatedSelectedPayment.billingAddress,
              });
            }
          } else {
            if (!foundPayment) {
              updatedPayments.push({
                ...updatedSelectedPayment,
                networkType: convertAurusCardType(paymentData.networkType),
              });
            }

            await profileService.post(`/customers/${clientId}/payments`, foundPayment || updatedSelectedPayment, {
              httpClientOptions: { handleErrors: false },
              clientId,
            });
          }
        }

        dispatch({
          type: actionTypes.CHANGE_PAYMENTS_SUCCESS,
          payload: updatedPayments,
        });
        return 'success';
      } catch (err) {
        dispatch({
          type: actionTypes.CHANGE_PAYMENTS_ERROR,
          payload: 'Not able to add card, please try again later',
        });
      }
    }
  };

export const getClientLoyaltyData = (allKiNumbers, plccKiNumbers, nonPlccKiNumbers) => async (dispatch) => {
  try {
    dispatch({
      type: actionTypes.CLIENT_LOYALTY_LOADING,
    });

    const params = {};

    if (allKiNumbers?.length) {
      params.kiNumbers = allKiNumbers.join(',');
    }

    const res = await profileService.get('/loyalty', { params });
    const { loyaltyAssets, nonPlccAssets } = splitPlccAndNoPlccKiNumbers(
      res.data.loyaltyAssets,
      plccKiNumbers,
      nonPlccKiNumbers,
    );

    dispatch({
      type: actionTypes.CLIENT_LOYALTY_SUCCESS,
      payload: {
        ...res.data,
        loyaltyAssets,
        nonPlccAssets,
      },
    });
    dispatch({
      type: actionTypes.ADD_LOYALTY_CARD_PAYMENTS,
      payload: res.data?.loyaltyAssets || {},
    });
  } catch (error) {
    dispatch({
      type: actionTypes.CLIENT_LOYALTY_ERROR,
      payload: error,
    });
  }
};

export const getSaltNumber = (cardNumber) => async (dispatch, getState) => {
  const { user = {} } = getState();
  const { nmid } = user.data || {};

  try {
    dispatch({
      type: actionTypes.GET_BARCODE_SALT_NUMBER_LOADING,
    });

    const response = await profileService.get(`/salt/?user=${nmid}&giftCardNumber=${cardNumber}`);

    if (response.data?.status === 'failed') {
      throw response.data;
    }

    dispatch({
      type: actionTypes.GET_BARCODE_SALT_NUMBER_SUCCESS,
      payload: response.data?.saltRes?.salt,
    });
  } catch (err) {
    const error = err.response?.data?.message || err.message;
    dispatch({
      type: actionTypes.GET_BARCODE_SALT_NUMBER_ERROR,
      payload: error,
    });
  }
};

export const clearCMDProfileData = () => ({
  type: actionTypes.CLEAR_CMD_PROFILE_DATA,
});

export const getMessagesList = (clientId, associateId) => async () => {
  try {
    const { data } = await conversationService.get(`getInboxDetails/${associateId}/ExternalEntity/${clientId}`);
    return data.Items || [];
  } catch (error) {
    return [];
  }
};

export const getProfileDataFromClientId =
  ({ clientId, brand, isClientInformationFromUcaEnabled }) =>
  async (dispatch) => {
    try {
      const {
        data: { cmdUri },
      } = await cmdProfileService.get(`/profiles/${clientId}?sourceName=${brand}`);

      dispatch(getCMDProfile(cmdUri, { isClientInformationFromUcaEnabled }));
    } catch (error) {
      dispatch({
        type: actionTypes.CMD_PROFILE_ERROR,
        payload: error,
      });
    }
  };

export const createFullClientsProfile = (formValues, bgAccountCreationFlowEnabled) => async (dispatch) => {
  const maxMatchesAmount = 100;
  const { email, phone } = formValues;

  try {
    const clientSearchMatches = await clientsCentralSearchServiceV2.get('customersByContact', {
      params: {
        email,
        phone,
        size: maxMatchesAmount,
      },
    });
    const customers = clientSearchMatches.data?.data.customers || [];

    if (customers.length) {
      return {
        status: 'exists',
        data: { customers },
      };
    }
  } catch (e) {
    dispatch(
      addNotification({
        id: `create-profile-${Date.now()}`,
        message: 'Error during client profile creation. Existed profiles can not be retrieved.',
        type: 'error',
      }),
    );

    return {
      status: 'error',
    };
  }

  try {
    const res = bgAccountCreationFlowEnabled
      ? await cmdProfileServiceV3.post('/profiles', formValues)
      : await cmdProfileService.post('/profiles', formValues);

    dispatch(
      addNotification({
        id: `create-profile-${Date.now()}`,
        message: 'Client profile successfully created',
        type: 'success',
      }),
    );

    return {
      status: 'created',
      data: res.data,
    };
  } catch (e) {
    dispatch(
      addNotification({
        id: `create-profile-${Date.now()}`,
        message: 'Error during client profile creation',
        type: 'error',
      }),
    );

    return {
      status: 'error',
    };
  }
};

export const updateClientStylePreferences =
  ({ newState, pathToUpdate, forcePost, options: { changeToSaSaved = false, changeToUneditable = false } }) =>
  async (dispatch, getState) => {
    dispatch({
      type: actionTypes.UPDATE_CLIENT_STYLE_PREFERENCES_LOADING,
    });
    const {
      clientsModule: {
        clientDetails: {
          profileData: { cmdUri, style, ucaProfiles },
        },
      },
      user: {
        data: { brand },
      },
    } = getState();

    try {
      const payload = _set({ ...style }, `${pathToUpdate}.data`, newState);

      if (changeToSaSaved) {
        _unset(payload, `${pathToUpdate}.systemGenerated`);
      }

      if (changeToUneditable) {
        _set(payload, `${pathToUpdate}.editable`, false);
      }

      const body = forcePost
        ? retrieveAllValuesFromPreferences(payload)
        : retrieveAttributeValuesFromPreferences(pathToUpdate, newState);

      const brandProfiles = ucaProfiles.filter(({ sourceName }) => sourceName.toLowerCase() === brand.toLowerCase());

      const requests = brandProfiles.map((ucaProfile) => {
        // POST method is required if the client profile has no style preference records.
        const method = forcePost || style.emptyProfiles.includes(ucaProfile.profileId) ? 'post' : 'put';
        return profileService[method](`/customers/${cmdUri}/prefs`, {
          ...body,
          ucaProfile,
        });
      });

      await Promise.all(requests);

      dispatch({
        type: actionTypes.UPDATE_CLIENT_STYLE_PREFERENCES_SUCCESS,
        payload,
      });
    } catch {
      dispatch({
        type: actionTypes.UPDATE_CLIENT_STYLE_PREFERENCES_ERROR,
      });
    }
  };

export const updateAdditionalClientData = (customerId, data) => async (dispatch, getState) => {
  const {
    clientsModule: {
      clientDetails: {
        profileData: { ucaProfiles },
      },
    },
    user: {
      data: { brand },
    },
  } = getState();
  dispatch({ type: actionTypes.UPDATE_ADDITIONAL_DATA_LOADING });
  try {
    const updateInfoData = await profileService.put(`/customers/${customerId}/info`, {
      ...data,
      ucaProfiles: ucaProfiles.filter(({ sourceName }) => sourceName.toLowerCase() === brand.toLowerCase()),
    });
    let res = [];
    if (updateInfoData) {
      const additionalQueryString = '&internalStorage=true';
      res = await cmdProfileServiceV3.get(`profiles/${customerId}?fields=basic${additionalQueryString}`);
    }
    dispatch({
      type: actionTypes.CMD_PROFILE_DETAILS_SUCCESS,
      payload: {
        ...res.data,
        cmdUri: customerId,
      },
    });
    dispatch({
      type: actionTypes.UPDATE_ADDITIONAL_DATA_SUCCESS,
      payload: {
        ...res.data,
        cmdUri: customerId,
      },
    });
    if (data.birthday) {
      dispatch(fetchClientReminders(customerId));
    }
  } catch (err) {
    dispatch({ type: actionTypes.UPDATE_ADDITIONAL_DATA_ERROR, payload: err });
  }
};

export const updateAddressTag = (customerId, data) => async (dispatch, getState) => {
  const {
    clientsModule: {
      clientDetails: {
        profileData: { ucaProfiles },
      },
    },
    user: {
      data: { brand },
    },
  } = getState();
  dispatch({ type: actionTypes.UPDATE_ADDRESS_TAG_LOADING });

  try {
    const updateInfoData = await profileService.put(`/customers/${customerId}/addresses`, {
      ...data,
      ucaProfiles: ucaProfiles.filter(({ sourceName }) => sourceName.toLowerCase() === brand.toLowerCase()),
    });
    let res = [];
    if (updateInfoData) {
      const additionalQueryString = '&internalStorage=true';
      res = await cmdProfileServiceV3.get(`profiles/${customerId}?fields=basic${additionalQueryString}`);
    }
    dispatch({
      type: actionTypes.CMD_PROFILE_DETAILS_SUCCESS,
      payload: {
        ...res.data,
        customerId,
      },
    });
    dispatch({
      type: actionTypes.UPDATE_ADDRESS_TAG_SUCCESS,
      payload: {
        ...res.data,
        cmdUri: customerId,
      },
    });
  } catch (err) {
    dispatch({ type: actionTypes.UPDATE_ADDRESS_TAG_ERROR, payload: err });
  }
};

export const updateRelatives = (customerId, data) => async (dispatch, getState) => {
  const {
    clientsModule: {
      clientDetails: {
        profileData: { ucaProfiles },
      },
    },
    user: {
      data: { brand },
    },
  } = getState();
  dispatch({ type: actionTypes.UPDATE_RELATIVES_LOADING });
  const requestPayload = {
    ...data,
    ucaProfiles: ucaProfiles.filter(({ sourceName }) => sourceName.toLowerCase() === brand.toLowerCase()),
  };
  const updateRequest = data?.relativeId
    ? profileService.put(`/customers/${customerId}/info/relatives/${data?.relativeId}`, requestPayload)
    : profileService.post(`/customers/${customerId}/info/relatives`, requestPayload);

  try {
    const updateInfoData = await updateRequest;
    let res = [];
    if (updateInfoData) {
      const additionalQueryString = '&internalStorage=true';
      res = await cmdProfileServiceV3.get(`profiles/${customerId}?fields=basic${additionalQueryString}`);
    }
    dispatch({
      type: actionTypes.CMD_PROFILE_DETAILS_SUCCESS,
      payload: {
        ...res.data,
        customerId,
      },
    });
    dispatch({
      type: actionTypes.UPDATE_RELATIVES_SUCCESS,
      payload: {
        ...res.data,
        cmdUri: customerId,
      },
    });
  } catch (err) {
    dispatch({ type: actionTypes.UPDATE_RELATIVES_ERROR, payload: err });
  }
};

export const updateSpecialDates = (customerId, data) => async (dispatch, getState) => {
  const {
    clientsModule: {
      clientDetails: {
        profileData: { ucaProfiles },
      },
    },
    user: {
      data: { brand },
    },
  } = getState();
  dispatch({ type: actionTypes.UPDATE_SPECIAL_DATES_LOADING });
  const requestPayload = {
    ...data,
    ucaProfiles: ucaProfiles.filter(({ sourceName }) => sourceName.toLowerCase() === brand.toLowerCase()),
  };
  const updateRequest = data?.dateId
    ? profileService.put(`/customers/${customerId}/info/dates/${data?.dateId}`, requestPayload)
    : profileService.post(`/customers/${customerId}/info/dates`, requestPayload);

  try {
    const updateInfoData = await updateRequest;
    let res = [];
    if (updateInfoData) {
      const additionalQueryString = '&internalStorage=true';
      res = await cmdProfileServiceV3.get(`profiles/${customerId}?fields=basic${additionalQueryString}`);
    }
    dispatch({
      type: actionTypes.CMD_PROFILE_DETAILS_SUCCESS,
      payload: {
        ...res.data,
        customerId,
      },
    });

    dispatch({
      type: actionTypes.UPDATE_SPECIAL_DATES_SUCCESS,
      payload: {
        ...res.data,
        cmdUri: customerId,
      },
    });
    dispatch(fetchClientReminders(customerId));
  } catch (err) {
    dispatch({ type: actionTypes.UPDATE_SPECIAL_DATES_ERROR, payload: err });
  }
};

export const markUpdatedPersonalData = (cmdId) => async (dispatch) => {
  try {
    const res = await profileService.post(`customers/${cmdId}/markUpdatedPersonalData`);
    if (res) {
      dispatch({
        type: actionTypes.CMD_MARK_UPDATE_PENDING,
        payload: true,
      });
    }
  } catch (err) {
    dispatch({
      type: actionTypes.CMD_MARK_UPDATE_PENDING_ERROR,
      payload: err,
    });
  }
};
